/**
 * Melody Trainer - the session shape, pure.
 *
 * The sibling of `pitch-training.ts`'s session fold.
 *
 * It USED to carry a session timing tendency beside the pitch score, and that is now deliberately
 * absent: `scoreTiming` measures every note from its own phrase's mean, so a per-attempt mean
 * onset error is identically zero and a session-level average of those zeroes would tell every
 * player on every device that their timing was even. See `score-timing.ts` for why the absolute
 * question cannot currently be asked at all (Bowie, 2026-09-13).
 *
 * Nothing here knows about audio. Everything an attempt needs to be *performed* - transposition,
 * audition, capture, scoring - happens above this module.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6
 */

/** The three input modes of §6's table. */
export const melodyTrainerModes = ['voice', 'instrument', 'midi'] as const

export type MelodyTrainerMode = (typeof melodyTrainerModes)[number]

export interface MelodyTrainerAttempt {
  /** `absolute.mean` for the attempt, or null when nothing was heard. */
  meanScore: number | null
}

export interface MelodyTrainerStats {
  /** Attempts that produced a score. An attempt nobody performed is not a zero. */
  scored: number
  total: number
  /** Mean of the scored attempts' pitch scores, 0-100. */
  meanPercent: number
}

const isNumber = (value: number | null): value is number => value !== null

export const computeMelodyTrainerStats = (attempts: MelodyTrainerAttempt[]): MelodyTrainerStats => {
  const scores = attempts.map(attempt => attempt.meanScore).filter(isNumber)

  const meanPercent =
    scores.length === 0 ? 0 : Math.round((scores.reduce((total, score) => total + score, 0) / scores.length) * 100)

  return { scored: scores.length, total: attempts.length, meanPercent }
}

/** Parse the `?mode=` query parameter. Anything unrecognised is null - the welcome screen asks. */
export const parseMelodyTrainerMode = (raw: string | null): MelodyTrainerMode | null =>
  melodyTrainerModes.find(mode => mode === raw) ?? null

/**
 * The two rungs of §12's ladder.
 *
 * `'sing-along'` is where a melody not yet practised starts: the standard teaching order is unison
 * with the instrument first and independence second, because a singer matches a sounding pitch
 * long before they can recall one. The first build shipped only the harder rung.
 */
export const practiceLevels = ['sing-along', 'sing-back'] as const

export type PracticeLevel = (typeof practiceLevels)[number]

/** Level 1 sounds the melody through the whole capture window; Level 2 stops before it. */
export const referenceSoundsDuringTake = (level: PracticeLevel): boolean => level === 'sing-along'

/**
 * The condition §5's headphone gate exists to prevent: something is sounding while a microphone is
 * open, so a speaker would be recorded and scored as the performance.
 *
 * Gated on the CAUSE and never on a mode's name, so a future mode inherits the right behaviour
 * without editing this rule - the same reasoning §11.2 applied when free-tempo capture needed no
 * headphones despite being an audio capture like any other: there was no click to bleed.
 * @see docs/superpowers/specs/2026-09-08-melody-plugin-design.md §12.2
 */
export const referenceSoundsIntoOpenMic = (input: { level: PracticeLevel; mode: MelodyTrainerMode }): boolean =>
  referenceSoundsDuringTake(input.level) && input.mode !== 'midi'
